/* Mahi Wairua — See it on your wall.
   Everything happens in the browser: the room photo is read with an object URL and is never uploaded,
   stored or sent anywhere. The result is always labelled an illustrative mockup. */
(function (root) {
  'use strict';

  /* ---------- Pure helpers (exported for tests) ---------- */
  const MWWall = {
    // "300 × 400 mm", "400 x 600mm", 12''x16'', 30x40cm -> {w, h} in millimetres (as written, before orientation)
    parseSize(label) {
      if (typeof label !== 'string') return null;
      const nums = (label.match(/\d+(?:\.\d+)?/g) || []).map(Number).filter(n => n > 0);
      if (nums.length < 2) return null;
      const lc = label.toLowerCase();
      let unit = 1;
      if (/''|"|”|″|\binch|\bin\b/.test(lc)) unit = 25.4;
      else if (/cm/.test(lc)) unit = 10;
      const w = Math.round(nums[0] * unit * 10) / 10, h = Math.round(nums[1] * unit * 10) / 10;
      if (w > 5000 || h > 5000) return null;
      return { w, h };
    },
    // Apply orientation: portrait = taller than wide.
    orient(size, orientation) {
      if (!size) return null;
      const long = Math.max(size.w, size.h), short = Math.min(size.w, size.h);
      return orientation === 'landscape' ? { w: long, h: short } : { w: short, h: long };
    },
    // Pixels per millimetre from two marker points and a measured length in cm.
    scaleFromReference(a, b, cm) {
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      const mm = Number(cm) * 10;
      if (!(len > 4) || !(mm > 0) || !isFinite(mm)) return null;
      return len / mm;
    },
    clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); },
    // Room photos: JPG, PNG and WebP open in every current browser. HEIC/HEIF and other formats get a clear message.
    photoKind(file) {
      if (!file) return 'none';
      const type = String(file.type || '').toLowerCase(), name = String(file.name || '').toLowerCase();
      if (/hei[cf]/.test(type) || /\.(heic|heif)$/.test(name)) return 'heic';
      if (/^image\/(jpeg|jpg|pjpeg|png|webp)$/.test(type)) return 'ok';
      if (!type && /\.(jpe?g|png|webp)$/.test(name)) return 'ok';
      return 'unsupported';
    },
    // Split text into lines no wider than maxW, keeping the customer's own line breaks.
    wrapText(text, maxW, measure) {
      const out = [];
      String(text).split('\n').forEach(par => {
        const words = par.split(/\s+/).filter(Boolean);
        if (!words.length) { out.push(''); return; }
        let line = '';
        words.forEach(w => {
          const t = line ? line + ' ' + w : w;
          if (measure(t) <= maxW) { line = t; return; }
          if (line) { out.push(line); line = ''; }
          if (measure(w) <= maxW) { line = w; return; }
          // A single word wider than the line is broken by character.
          let part = '';
          for (const ch of w) { if (part && measure(part + ch) > maxW) { out.push(part); part = ch; } else part += ch; }
          line = part;
        });
        out.push(line);
      });
      return out;
    },
    normalise(text) { return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(); },
    pickArtwork(images, variant, panelCount) {
      const candidates = panelCount > 1 ? images : images.filter(im => im.flat);
      if (!candidates.length) return null;
      if (variant && variant.img) {
        const path = src => String(src || '').split('?')[0];
        const hit = candidates.find(im => path(im.src) === path(variant.img));
        if (hit) return hit;
      }
      const design = MWWall.normalise(variant && variant.design);
      if (design) {
        const aliases = { 'maunga me te awa':'maunga', 'ngahere':'forest', 'pohutukawa':'coast', 'native forest':'forest', 'coastal sunset':'coast' };
        const key = aliases[design] || design;
        const match = candidates.find(im => MWWall.normalise(im.alt).startsWith(design) || MWWall.normalise(im.alt).includes(key));
        if (match) return match;
      }
      const size = MWWall.parseSize(variant && variant.size);
      if (size && candidates.some(im => /matariki/.test(MWWall.normalise(im.alt)))) {
        const match = candidates.find(im => im.src.includes(size.w + 'x' + size.h));
        if (match) return match;
      }
      return candidates[0];
    },
    // Keep the artwork centre inside the stage.
    clampCentre(cx, cy) { return { cx: MWWall.clamp(cx, 0.02, 0.98), cy: MWWall.clamp(cy, 0.02, 0.98) }; }
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = MWWall; return; }
  root.MWWall = MWWall;
  if (root.customElements && root.customElements.get('mahi-wall-preview')) return;

  /* ---------- Custom element ---------- */
  class MahiWallPreview extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const $ = s => this.querySelector(s), all = s => [...this.querySelectorAll(s)];
      this.$ = $;
      let cfg = {};
      try { cfg = JSON.parse(($('[data-wall-config]') || {}).textContent || '{}'); } catch (e) { cfg = {}; }
      this.cfg = { variants: cfg.variants || [], sizes: cfg.sizes || [], images: cfg.images || [], selectedImage: cfg.selectedImage || null, metal: !!cfg.metal, panelCount: cfg.panelCount || 1, panelCrops: cfg.panelCrops || [], rooms: cfg.rooms || [] };
      this.scope = this.closest('.shopify-section') || document;
      this.miniImages = [...this.querySelectorAll('[data-wall-mini-art]')];
      this.source = this.dataset.source || 'product';
      this.dialog = $('[data-wall-dialog]');
      this.stage = $('[data-wall-stage]');
      this.room = $('[data-wall-room]');
      this.plain = $('[data-wall-plain]');
      this.art = $('[data-wall-art]');
      this.artImg = $('[data-wall-art-img]');
      this.badge = $('[data-wall-badge]');
      this.scaleInput = $('[data-wall-scale]');
      this.sizeSelect = $('[data-wall-size]');
      this.status = $('[data-wall-status]');
      this.error = $('[data-wall-error]');
      this.state = { cx: 0.5, cy: 0.42, widthPct: 35, orientation: 'portrait', lockedOrientation: null, size: null, pxPerMm: null, refCm: null, roomUrl: null, artSrc: null, aspect: 0.75 };
      this.markers = { a: { x: 0.3, y: 0.7 }, b: { x: 0.7, y: 0.7 } };

      $('[data-wall-open]').addEventListener('click', e => { this._opener = e.currentTarget; this.open(); });
      all('[data-wall-room-open]').forEach(b => b.addEventListener('click', () => { this._opener = b; this.open(); this.useRoomScene(b.dataset.wallRoomOpen); }));
      all('[data-wall-room-choice]').forEach(b => b.addEventListener('click', () => this.useRoomScene(b.dataset.wallRoomChoice)));
      all('[data-wall-close]').forEach(b => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', e => { e.preventDefault(); this.close(); });
      this.dialog.addEventListener('click', e => { if (e.target === this.dialog) this.close(); });
      this.fileInput = $('[data-wall-file]');
      this.fileInput.addEventListener('change', e => { this.loadRoom(e.target.files && e.target.files[0]); e.target.value = ''; });
      $('[data-wall-retry]').addEventListener('click', () => this.fileInput.click());
      $('[data-wall-sample]').addEventListener('click', () => this.usePlainWall());
      this.scaleInput.addEventListener('input', () => { this.state.widthPct = Number(this.scaleInput.value); this.layout(); });
      this.sizeSelect.addEventListener('change', () => { this.state.size = MWWall.parseSize(this.sizeSelect.value); this.applyOrientation(); this.syncMeasurement(); this.updateAspect(); });
      all('[data-wall-orient]').forEach(b => b.addEventListener('click', () => {
        this.state.orientation = b.dataset.wallOrient; this._orientChosen = true;
        all('[data-wall-orient]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
        this.updateAspect();
      }));
      $('[data-wall-ref-start]').addEventListener('click', () => this.showMarkers());
      $('[data-wall-ref-apply]').addEventListener('click', () => this.applyReference());
      $('[data-wall-ref-clear]').addEventListener('click', () => this.clearReference());
      $('[data-wall-download]').addEventListener('click', () => this.download());
      this.bindDrag();
      this.bindKeys();
      this.bindMarkers();
      this.ro = 'ResizeObserver' in window ? new ResizeObserver(() => this.layout()) : null;
      if (this.ro) this.ro.observe(this.stage); else window.addEventListener('resize', () => this.layout());
      if (this.cfg.metal) {
        const update = () => {
          clearTimeout(this._refreshTimer);
          this._refreshTimer = setTimeout(() => {
            if (this.dialog.open) { this.setupSizes(); this.setupArtwork(); }
            else this.refreshMiniArtwork();
          }, 180);
        };
        this._onChange = update;
        this.scope.addEventListener('change', update);
        this.scope.addEventListener('input', update);
        this.scope.addEventListener('variant:change', update);
        this.refreshMiniArtwork();
        if (this.source === 'studio') [700, 1800, 3500].forEach(ms => setTimeout(() => { if (this.isConnected) this.refreshMiniArtwork(); }, ms));
        const gallery = this.scope.querySelector('product-gallery');
        const launch = this.querySelector('[data-wall-room-launch]');
        if (gallery && launch && this.source !== 'studio') {
          gallery.querySelectorAll('[data-wall-room-launch]').forEach(old => old.remove());
          this._movedLaunch = launch; gallery.appendChild(launch);
        }
      }
    }

    disconnectedCallback() { this.revokeRoom(); if (this.ro) this.ro.disconnect(); clearTimeout(this._refreshTimer); if (this._movedLaunch) this._movedLaunch.remove(); if (this._onChange) ['change','input','variant:change'].forEach(ev => this.scope.removeEventListener(ev, this._onChange)); }

    /* ----- open / close ----- */
    open() {
      this.hideError();
      this._orientChosen = false;
      this.setupSizes();
      this.setupArtwork();
      this.updateAspect();
      if (!this.state.roomUrl) this.usePlainWall(true);
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('mw-wall-lock');
      requestAnimationFrame(() => { this.layout(); this.art.focus({ preventScroll: true }); });
    }
    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close(); else this.dialog.removeAttribute('open');
      document.documentElement.classList.remove('mw-wall-lock');
      (this._opener || this.$('[data-wall-open]')).focus({ preventScroll: true });
    }

    /* ----- sizes & orientation ----- */
    productForm() {
      return this.closest('form') || this.scope.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    }
    currentVariant() {
      const form = this.productForm();
      const idInput = form ? form.querySelector('[name="id"]') : null;
      return (idInput && this.cfg.variants.find(x => String(x.id) === String(idInput.value))) || null;
    }
    currentSizeLabel() {
      if (this.source === 'studio') {
        const s = document.querySelector('[data-size]');
        return s ? s.value : null;
      }
      const v = this.currentVariant();
      if (v) return v.size || null;
      return this.cfg.sizes.length === 1 ? this.cfg.sizes[0] : null;
    }
    // Orientation the shopper already chose on the product form (personalised photo prints).
    formOrientation() {
      const form = this.productForm();
      const sel = form && form.querySelector('[name="properties[Orientation]"]');
      const v = sel ? String(sel.value || '').toLowerCase() : '';
      return /landscape/.test(v) ? 'landscape' : /portrait/.test(v) ? 'portrait' : null;
    }
    setupSizes() {
      const labels = this.source === 'studio'
        ? [...(document.querySelectorAll('[data-size] option') || [])].map(o => o.value)
        : this.cfg.sizes;
      const parsed = labels.filter(l => MWWall.parseSize(l));
      const field = this.$('[data-wall-size-field]'), orient = this.$('[data-wall-orient-field]');
      this.sizeSelect.innerHTML = '';
      parsed.forEach(l => this.sizeSelect.add(new Option(l, l)));
      field.hidden = parsed.length < 2;
      // Only a size that is actually selected and has real dimensions is used. Unknown sizes stay "not to scale".
      const current = this.currentSizeLabel();
      if (current && parsed.includes(current)) { this.sizeSelect.value = current; this.state.size = MWWall.parseSize(current); }
      else { this.state.size = null; field.hidden = true; }
      this.state.lockedOrientation = this.source === 'studio' ? 'landscape' : this.formOrientation();
      this.applyOrientation();
      this.syncMeasurement();
    }
    // Studio artwork is always landscape. A product form's Portrait/Landscape choice wins; otherwise follow the size.
    applyOrientation() {
      const orient = this.$('[data-wall-orient-field]');
      if (this.state.lockedOrientation) { this.state.orientation = this.state.lockedOrientation; orient.hidden = true; }
      else {
        orient.hidden = !this.state.size;
        if (this.state.size && !this._orientChosen) {
          const s = this.state.size; this.state.orientation = s.w > s.h ? 'landscape' : 'portrait';
        }
      }
      this.syncOrient();
    }
    // Measurement-based sizing needs real dimensions; without them it is switched off and the label stays.
    syncMeasurement() {
      const panel = this.$('[data-wall-scale-panel]'), off = this.$('[data-wall-scale-off]');
      const known = !!this.state.size && !this.state.roomKey && this.cfg.panelCount < 2;
      panel.hidden = !known;
      if (!known) { panel.open = false; this.$('[data-wall-ref]').hidden = true; }
      panel.querySelectorAll('input, button').forEach(el => { el.disabled = !known; });
      if (off) off.hidden = known || !!this.state.roomKey;
      if (!known && this.state.pxPerMm) this.clearReference(true);
    }
    syncOrient() { [...this.querySelectorAll('[data-wall-orient]')].forEach(x => x.setAttribute('aria-pressed', String(x.dataset.wallOrient === this.state.orientation))); }
    updateAspect() {
      const s = MWWall.orient(this.state.size, this.state.orientation);
      if (this.cfg.metal && this.artImg.naturalWidth) this.state.aspect = this.artImg.naturalWidth / this.artImg.naturalHeight;
      else if (s) this.state.aspect = s.w / s.h;
      else if (this.artImg.naturalWidth) this.state.aspect = this.artImg.naturalWidth / this.artImg.naturalHeight;
      if (this.state.pxPerMm && !s) this.clearReference(true);
      this.layout();
    }

    /* ----- artwork ----- */
    setupArtwork() {
      const thumbsField = this.$('[data-wall-thumbs-field]'), thumbs = this.$('[data-wall-thumbs]');
      const noteEl = this.$('[data-wall-art-note]');
      const note = t => { if (noteEl) { noteEl.textContent = t || ''; noteEl.hidden = !t; } };
      thumbs.innerHTML = '';
      thumbsField.hidden = true;
      if (this.source === 'studio') {
        // The Studio canvas always holds the flat design (background, heading and names), even in room view.
        note('Showing your design with your chosen background, font and wording.');
        this.captureStudio(true);
        // The background photo may still be loading; refresh the capture shortly after opening.
        [500, 1500].forEach(ms => setTimeout(() => { if (this.dialog.open) this.captureStudio(false); }, ms));
        return;
      }
      // Personalised text and flag prints: use the on-page preview, with the chosen background and wording.
      const preview = this.scope.querySelector('.mw-text-preview, .mw-flag-preview');
      if (preview && preview.getBoundingClientRect().width) {
        this.state.lockedOrientation = preview.classList.contains('mw-flag-preview') ? 'landscape' : 'portrait';
        this.applyOrientation();
        note('Showing your chosen background and wording.');
        const job = this._artJob = (this._artJob || 0) + 1;
        this.renderDomPreview(preview).then(src => {
          if (job !== this._artJob) return;
          if (src) { this.setArt(src); return; }
          note('Your wording could not be added to this mockup, so the product image is shown instead.');
          this.useProductImage(thumbs, thumbsField);
        });
        return;
      }
      note('');
      this.useProductImage(thumbs, thumbsField);
    }
    refreshMiniArtwork() {
      if (this.source === 'studio') { this.captureStudio(false); return; }
      const preview = this.scope.querySelector('.mw-text-preview, .mw-flag-preview');
      if (preview && preview.getBoundingClientRect().width) {
        const job = this._miniJob = (this._miniJob || 0) + 1;
        this.renderDomPreview(preview).then(src => { if (src && job === this._miniJob) this.setArt(src); });
      } else this.useArtwork(MWWall.pickArtwork(this.cfg.images, this.currentVariant(), this.cfg.panelCount));
    }
    useArtwork(im) {
      if (!im) return;
      if (this.cfg.panelCount < 2) { this.setArt(im.src); return; }
      const rects = this.cfg.panelCrops[im.index];
      if (!rects || rects.length !== this.cfg.panelCount) { this.art.hidden = true; this.say('The panel artwork could not be loaded.'); return; }
      const job = this._panelJob = (this._panelJob || 0) + 1;
      this.art.hidden = true;
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (job !== this._panelJob) return;
        try {
          const H = 600, gap = 28;
          const widths = rects.map(r => Math.round(H * r[2] / r[3]));
          const c = document.createElement('canvas'); c.width = widths.reduce((a,b) => a+b,0) + gap*(rects.length-1); c.height = H;
          const ctx = c.getContext('2d'); let x=0;
          rects.forEach((r,i) => { ctx.drawImage(img, r[0]*img.naturalWidth/im.w, r[1]*img.naturalHeight/im.h, r[2]*img.naturalWidth/im.w, r[3]*img.naturalHeight/im.h, x,0,widths[i],H); x+=widths[i]+gap; });
          this.setArt(c.toDataURL('image/png'));
        } catch(e) { this.art.hidden = true; this.say('The panel artwork could not be loaded. Please try again.'); }
      };
      img.onerror = () => { if (job === this._panelJob) this.say('The panel artwork could not be loaded. Please try again.'); };
      img.src = im.src;
    }
    captureStudio(first) {
      const c = document.querySelector('[data-canvas]');
      let src = null;
      try { src = c && c.width ? c.toDataURL('image/png') : null; } catch (e) { src = null; }
      if (src) { if (src !== this.state.artSrc) this.setArt(src); }
      else if (first) this.showError('The design preview could not be loaded. Please close this and try again once your design has finished drawing.');
    }
    // Product artwork: the selected variant's image first, then the product's featured image.
    useProductImage(thumbs, thumbsField) {
      const v = this.currentVariant();
      const picked = MWWall.pickArtwork(this.cfg.images, v, this.cfg.panelCount);
      const imgs = this.cfg.metal ? (this.cfg.panelCount > 1 ? this.cfg.images : this.cfg.images.filter(im => im.flat)) : this.cfg.images;
      const first = this.cfg.metal ? (picked && picked.src) : ((v && v.img) || this.cfg.selectedImage || (imgs[0] && imgs[0].src));
      thumbsField.hidden = imgs.length < 2;
      imgs.forEach((im, i) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'mw-wall__thumb';
        b.setAttribute('aria-label', 'Use image ' + (i + 1) + ': ' + (im.alt || ''));
        b.innerHTML = '<img alt="" loading="lazy">';
        b.querySelector('img').src = im.thumb;
        b.addEventListener('click', () => { this.useArtwork(im); [...thumbs.children].forEach(x => x.setAttribute('aria-pressed', String(x === b))); });
        thumbs.appendChild(b);
      });
      if (first) { if (this.cfg.metal) this.useArtwork(picked); else this.setArt(first); }
      else if (this.cfg.metal) { this.art.hidden = true; this.say('A flat artwork image is needed before this product can be previewed.'); }
      [...thumbs.children].forEach((x, i) => x.setAttribute('aria-pressed', String(!!imgs[i] && imgs[i].src === first)));
    }
    // Draw an on-page preview (background image plus the customer's wording) onto a canvas, locally.
    renderDomPreview(box) {
      const img = box.querySelector('img');
      const copy = box.querySelector('[data-flag-copy], .mw-text-preview-copy');
      const br = box.getBoundingClientRect();
      if (!img || !br.width || !br.height) return Promise.resolve(null);
      const k = 1200 / br.width;
      return new Promise(resolve => {
        const bg = new Image();
        bg.crossOrigin = 'anonymous';
        bg.onload = () => {
          try {
            const c = document.createElement('canvas');
            c.width = 1200; c.height = Math.round(br.height * k);
            const ctx = c.getContext('2d');
            ctx.fillStyle = '#f6f1e7'; ctx.fillRect(0, 0, c.width, c.height);
            const ir = img.getBoundingClientRect();
            const dx = (ir.left - br.left) * k, dy = (ir.top - br.top) * k, dw = ir.width * k, dh = ir.height * k;
            const sc = Math.max(dw / bg.naturalWidth, dh / bg.naturalHeight), sw = dw / sc, sh = dh / sc;
            ctx.drawImage(bg, (bg.naturalWidth - sw) / 2, (bg.naturalHeight - sh) / 2, sw, sh, dx, dy, dw, dh);
            if (copy) {
              const cr = copy.getBoundingClientRect();
              ctx.save();
              ctx.beginPath(); ctx.rect((cr.left - br.left) * k, (cr.top - br.top) * k, cr.width * k, cr.height * k); ctx.clip();
              [...copy.children].forEach(el => {
                const text = el.textContent || '';
                const cs = getComputedStyle(el);
                if (!text.trim() || el.hidden || cs.display === 'none') return;
                const r = el.getBoundingClientRect();
                const fs = parseFloat(cs.fontSize) * k;
                const lh = (parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.3) * k;
                ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + fs + 'px ' + cs.fontFamily;
                ctx.fillStyle = cs.color;
                ctx.textBaseline = 'top';
                const maxW = r.width * k, left = (r.left - br.left) * k;
                const centre = cs.textAlign === 'center';
                ctx.textAlign = centre ? 'center' : 'left';
                let y = (r.top - br.top) * k + (lh - fs) / 2;
                MWWall.wrapText(text, maxW, t => ctx.measureText(t).width).forEach(line => { ctx.fillText(line, centre ? left + maxW / 2 : left, y); y += lh; });
              });
              ctx.restore();
            }
            resolve(c.toDataURL('image/jpeg', 0.92));
          } catch (e) { resolve(null); }
        };
        bg.onerror = () => resolve(null);
        const src = img.currentSrc || img.src;
        // A separate URL avoids reusing a cached copy that was fetched without CORS.
        bg.src = /^(data|blob):/.test(src) ? src : src + (src.indexOf('?') === -1 ? '?' : '&') + 'mw-wall=1';
      });
    }
    setArt(src) {
      this.state.artSrc = src;
      this.art.hidden = false;
      this.art.classList.toggle('mw-wall__art--panels', this.cfg.panelCount > 1);
      this.miniImages.forEach(im => { im.src = src; im.hidden = false; im.classList.toggle('is-panels', this.cfg.panelCount > 1); });
      this.artImg.crossOrigin = /^data:|^blob:/.test(src) ? null : 'anonymous';
      this.artImg.onload = () => this.updateAspect();
      this.artImg.src = src;
    }

    /* ----- room photo (local only) ----- */
    revokeRoom() { if (this.state.roomUrl) { if (this.state.roomUrl.startsWith('blob:')) URL.revokeObjectURL(this.state.roomUrl); this.state.roomUrl = null; } }
    syncRoomButtons(key) { this.querySelectorAll('[data-wall-room-choice]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.wallRoomChoice === key))); }
    useRoomScene(key) {
      const scene = this.cfg.rooms.find(r => r.key === key);
      if (!scene) return;
      this.hideError(); this.revokeRoom(); this.clearReference(true);
      this.state.roomUrl = scene.src; this.state.roomKey = key;
      this.syncRoomButtons(key);
      this.room.crossOrigin = 'anonymous';
      this.room.alt = scene.name + ' illustrative room scene';
      this.room.hidden = true; this.plain.hidden = false;
      this.room.onload = () => {
        if (this.state.roomKey !== key) return;
        this.room.hidden = false; this.plain.hidden = true;
        this.stage.style.aspectRatio = this.room.naturalWidth + ' / ' + this.room.naturalHeight;
        this.state.cx = scene.cx; this.state.cy = scene.cy;
        this.setWidth(this.cfg.panelCount > 1 ? 43 : (this.state.aspect > 1 ? 32 : scene.width));
        this.say('Showing the ' + scene.name.toLowerCase() + ' scene. Illustrative mockup, not to scale.');
      };
      this.room.onerror = () => { this.usePlainWall(true); this.showError('That room scene could not load. Please try another scene.'); };
      this.room.src = scene.src + '&width=1600';
      this.$('[data-wall-scale-panel]').hidden = true;
      this.$('[data-wall-scale-off]').hidden = true;
    }
    loadRoom(file) {
      this.hideError();
      if (!file) return;
      const kind = MWWall.photoKind(file);
      if (kind === 'heic') { this.showError('HEIC photos can’t be opened here. Please choose a JPG or PNG photo, or save this one as a JPG first.', true); return; }
      if (kind !== 'ok') { this.showError('That file type can’t be used. Please choose a JPG or PNG photo.', true); return; }
      if (file.size > 30 * 1024 * 1024) { this.showError('That photo is larger than 30 MB. Please choose a smaller JPG or PNG photo.', true); return; }
      this.revokeRoom(); this.state.roomKey = null; this.syncRoomButtons(null);
      this.room.crossOrigin = null; this.room.alt = 'Your wall photo';
      const url = URL.createObjectURL(file);
      this.state.roomUrl = url;
      this.room.onload = () => {
        this.room.hidden = false; this.plain.hidden = true;
        this.stage.style.aspectRatio = this.room.naturalWidth + ' / ' + this.room.naturalHeight;
        this.clearReference(true); this.syncMeasurement();
        this.layout();
        this.say('Photo added. Drag the artwork into place.');
      };
      this.room.onerror = () => { this.revokeRoom(); this.usePlainWall(true); this.showError('That photo couldn’t be opened on this device. Please choose a JPG or PNG photo.', true); };
      this.room.src = url;
    }
    usePlainWall(silent) {
      this.revokeRoom(); this.state.roomKey = null; this.syncRoomButtons(null); this.syncMeasurement();
      this.room.onload = null; this.room.onerror = null;
      this.room.removeAttribute('src'); this.room.hidden = true; this.plain.hidden = false;
      this.stage.style.aspectRatio = '4 / 3';
      this.clearReference(true);
      this.layout();
      if (!silent) this.say('Showing a plain wall.');
    }

    /* ----- layout ----- */
    layout() {
      const W = this.stage.clientWidth, H = this.stage.clientHeight;
      if (!W || !H) return;
      const s = MWWall.orient(this.state.size, this.state.orientation);
      let wPx;
      if (this.state.pxPerMm && s) { wPx = s.w * this.state.pxPerMm * (W / this._refStageW); }
      else wPx = W * this.state.widthPct / 100;
      let hPx = wPx / (this.state.aspect || 0.75);
      if (hPx > H * 0.98) { hPx = H * 0.98; wPx = hPx * this.state.aspect; }
      const c = MWWall.clampCentre(this.state.cx, this.state.cy);
      this.state.cx = c.cx; this.state.cy = c.cy;
      Object.assign(this.art.style, { width: wPx + 'px', height: hPx + 'px', left: (c.cx * W - wPx / 2) + 'px', top: (c.cy * H - hPx / 2) + 'px' });
      this.scaleInput.disabled = !!this.state.pxPerMm;
      this.badge.textContent = this.state.pxPerMm
        ? 'Illustrative mockup · approximate size from your ' + this.state.refCm + ' cm measurement'
        : 'Illustrative mockup · not to scale';
      this.drawRefLine();
    }

    /* ----- dragging & pinch ----- */
    bindDrag() {
      const pts = new Map();
      let start = null;
      const pos = e => { const r = this.stage.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, r }; };
      this.art.addEventListener('pointerdown', e => {
        e.preventDefault();
        this.art.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, pos(e));
        const resize = e.target.closest('[data-wall-handle]');
        start = { cx: this.state.cx, cy: this.state.cy, w: this.state.widthPct, resize, p: pos(e), dist: null };
        if (pts.size === 2) { const [a, b] = [...pts.values()]; start.dist = Math.hypot(a.x - b.x, a.y - b.y); }
      });
      this.art.addEventListener('pointermove', e => {
        if (!start || !pts.has(e.pointerId)) return;
        pts.set(e.pointerId, pos(e));
        const p = pos(e);
        if (pts.size === 2 && start.dist && !this.state.pxPerMm) {
          const [a, b] = [...pts.values()];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          this.setWidth(start.w * d / start.dist);
        } else if (start.resize && !this.state.pxPerMm) {
          const dx = (p.x - start.p.x) * 100 * 2;
          this.setWidth(start.w + dx);
        } else if (!start.resize) {
          this.state.cx = start.cx + (p.x - start.p.x);
          this.state.cy = start.cy + (p.y - start.p.y);
          this.layout();
        }
      });
      const end = e => { pts.delete(e.pointerId); if (!pts.size) start = null; else { const rest = [...pts.values()][0]; start = { cx: this.state.cx, cy: this.state.cy, w: this.state.widthPct, p: rest, dist: null }; } };
      this.art.addEventListener('pointerup', end);
      this.art.addEventListener('pointercancel', end);
    }
    setWidth(pct) {
      this.state.widthPct = MWWall.clamp(Math.round(pct), 8, 90);
      this.scaleInput.value = this.state.widthPct;
      this.layout();
    }
    bindKeys() {
      this.art.addEventListener('keydown', e => {
        const step = e.shiftKey ? 0.05 : 0.01;
        const map = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
        if (map[e.key]) { e.preventDefault(); this.state.cx += map[e.key][0]; this.state.cy += map[e.key][1]; this.layout(); }
        else if ((e.key === '+' || e.key === '=') && !this.state.pxPerMm) { e.preventDefault(); this.setWidth(this.state.widthPct + 2); }
        else if (e.key === '-' && !this.state.pxPerMm) { e.preventDefault(); this.setWidth(this.state.widthPct - 2); }
      });
    }

    /* ----- reference measurement ----- */
    showMarkers() {
      this.$('[data-wall-ref]').hidden = false;
      this.placeMarkers();
      this.say('Drag the two gold markers to the ends of the thing you measured.');
    }
    placeMarkers() {
      ['a', 'b'].forEach(k => {
        const m = this.$('[data-wall-marker="' + k + '"]');
        m.style.left = (this.markers[k].x * 100) + '%'; m.style.top = (this.markers[k].y * 100) + '%';
      });
      this.drawRefLine();
    }
    drawRefLine() {
      const line = this.$('[data-wall-ref-line]');
      if (!line) return;
      const W = this.stage.clientWidth, H = this.stage.clientHeight;
      line.setAttribute('x1', this.markers.a.x * W); line.setAttribute('y1', this.markers.a.y * H);
      line.setAttribute('x2', this.markers.b.x * W); line.setAttribute('y2', this.markers.b.y * H);
    }
    bindMarkers() {
      this.querySelectorAll('[data-wall-marker]').forEach(m => {
        const k = m.dataset.wallMarker;
        m.addEventListener('pointerdown', e => { e.preventDefault(); m.setPointerCapture(e.pointerId); m._drag = true; });
        m.addEventListener('pointermove', e => {
          if (!m._drag) return;
          const r = this.stage.getBoundingClientRect();
          this.markers[k] = { x: MWWall.clamp((e.clientX - r.left) / r.width, 0, 1), y: MWWall.clamp((e.clientY - r.top) / r.height, 0, 1) };
          this.placeMarkers();
        });
        const stop = () => { m._drag = false; };
        m.addEventListener('pointerup', stop); m.addEventListener('pointercancel', stop);
      });
    }
    applyReference() {
      const cm = Number(this.$('[data-wall-ref-cm]').value);
      if (this.$('[data-wall-ref]').hidden) { this.showMarkers(); return; }
      if (!this.state.size) { this.say('This product has no listed size, so an approximate scale is not available.'); return; }
      const W = this.stage.clientWidth, H = this.stage.clientHeight;
      const a = { x: this.markers.a.x * W, y: this.markers.a.y * H }, b = { x: this.markers.b.x * W, y: this.markers.b.y * H };
      const ppm = MWWall.scaleFromReference(a, b, cm);
      if (!ppm) { this.say('Enter the measured length in centimetres and move the markers apart.'); return; }
      this.state.pxPerMm = ppm; this.state.refCm = cm; this._refStageW = W;
      this.$('[data-wall-ref-clear]').hidden = false;
      this.$('[data-wall-ref]').hidden = true;
      this.layout();
      this.say('Artwork sized from your measurement. This is approximate.');
    }
    clearReference(silent) {
      this.state.pxPerMm = null; this.state.refCm = null;
      const c = this.$('[data-wall-ref-clear]'); if (c) c.hidden = true;
      this.layout();
      if (!silent) this.say('Measurement cleared. You can resize freely.');
    }

    /* ----- download (composited locally) ----- */
    download() {
      const W = this.stage.clientWidth, H = this.stage.clientHeight;
      const roomOk = !this.room.hidden && this.room.naturalWidth;
      const outW = Math.min(2000, roomOk ? this.room.naturalWidth : 1600);
      const k = outW / W, outH = Math.round(H * k);
      const c = document.createElement('canvas'); c.width = outW; c.height = outH;
      const ctx = c.getContext('2d');
      if (roomOk) ctx.drawImage(this.room, 0, 0, outW, outH);
      else { const g = ctx.createLinearGradient(0, 0, 0, outH); g.addColorStop(0, '#efe8da'); g.addColorStop(0.78, '#e4dccb'); g.addColorStop(0.78, '#cbb894'); g.addColorStop(1, '#b9a47c'); ctx.fillStyle = g; ctx.fillRect(0, 0, outW, outH); }
      const r = this.art.getBoundingClientRect(), s = this.stage.getBoundingClientRect();
      const x = (r.left - s.left) * k, y = (r.top - s.top) * k, w = r.width * k, h = r.height * k;
      ctx.save(); ctx.shadowColor = 'rgba(0,0,0,.35)'; ctx.shadowBlur = 18 * k; ctx.shadowOffsetY = 6 * k;
      if (this.cfg.panelCount < 2) { ctx.fillStyle = '#fff'; ctx.fillRect(x, y, w, h); } ctx.restore();
      try { ctx.drawImage(this.artImg, x, y, w, h); } catch (e) { /* drawn below as failure */ }
      const label = this.badge.textContent + ' — Mahi Wairua';
      const fs = Math.max(16, Math.round(outW / 60));
      ctx.font = '600 ' + fs + 'px Montserrat, Arial, sans-serif';
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(32,37,34,.82)'; ctx.fillRect(outW - tw - fs * 1.6, outH - fs * 2.4, tw + fs * 1.2, fs * 1.8);
      ctx.fillStyle = '#f6f1e7'; ctx.fillText(label, outW - tw - fs, outH - fs * 1.1);
      try {
        c.toBlob(b => {
          if (!b) { this.say('Download is not available on this device. You can take a screenshot instead.'); return; }
          const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'mahi-wairua-wall-mockup.png';
          document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
          this.say('Mockup downloaded to this device.');
        }, 'image/png');
      } catch (e) { this.say('Download is not available for this image. You can take a screenshot instead.'); }
    }

    /* ----- messages ----- */
    say(t) { this.status.textContent = t; }
    showError(t, offerRetry) {
      this.$('[data-wall-error-text]').textContent = t;
      this.$('[data-wall-retry]').hidden = !offerRetry;
      this.error.hidden = false;
    }
    hideError() { this.error.hidden = true; this.$('[data-wall-error-text]').textContent = ''; this.$('[data-wall-retry]').hidden = true; }
  }
  customElements.define('mahi-wall-preview', MahiWallPreview);
})(typeof window !== 'undefined' ? window : this);

